/**
 * Tenant referral programme — the shared vocabulary.
 *
 * The rules, in one place because they are quoted on four screens (the tenant
 * hub, the share message, the apply form and the admin card) and must not drift
 * between them:
 *
 *   · EUR 100 off the referrer's next rent per successful referral
 *   · a free month of rent at 10 successful referrals
 *   · "successful" = the referred person's lease is SIGNED
 *
 * The stage ladder mirrors the `stage` CHECK constraint in
 * supabase/migrations/20260907170000_tenant_referrals.sql exactly. Four stages,
 * deliberately not five: the programme is CODE-ONLY (no tracked referral link),
 * so nothing can tell us a friend exists before they apply, and a "shared"
 * stage would be a rung the data can never reach.
 */

export const REFERRAL_REWARD_CENTS = 10_000;
export const REFERRAL_FREE_MONTH_GOAL = 10;

export const REFERRAL_STAGES = [
  "applied",
  "approved",
  "signed",
  "rewarded",
] as const;

export type ReferralStage = (typeof REFERRAL_STAGES)[number];

export interface ReferralItem {
  id: string;
  stage: ReferralStage;
  code_used: string;
  applied_at: string | null;
  approved_at: string | null;
  signed_at: string | null;
  rewarded_at: string | null;
  closed_at: string | null;
  reward_amount_cents: number;
  created_at: string;
  /** First name + last initial. Never a full surname, never contact details. */
  referred_name: string | null;
  /** "Zimmer 4B · Wilhelmstraße 12", or null if the room is gone. */
  room_label: string | null;
  /** Due date of the rent the reward landed on. Null until it is applied. */
  reward_due_date: string | null;
  /**
   * Earned, but there is currently no upcoming rent row for the EUR 100 to
   * land on — the usual cause is a referrer who has moved out and will not be
   * billed again. Computed live by `my_referrals()`; it clears itself the
   * moment a rent row is materialised.
   */
  reward_stalled?: boolean;
}

/** One free month earned by hitting a 10-referral milestone. */
export interface ReferralBonus {
  id: string;
  /** 10, 20, 30 … which threshold this represents. */
  milestone: number;
  /** Rent actually waived. Null until it lands on a month. */
  amount_cents: number | null;
  earned_at: string;
  applied_at: string | null;
  /** Due date of the month that was waived. Null until applied. */
  waived_due_date: string | null;
}

export interface MyReferrals {
  items: ReferralItem[];
  successful: number;
  in_progress: number;
  earned_cents: number;
  /** Referrals needed for the free month — 10. */
  goal: number;
  /**
   * Progress toward the free month, CAPPED at `goal`. One free month per tenant,
   * so this fills and stays full rather than wrapping into a second cycle.
   */
  toward_next: number;
  /** 0 or 1 — the free month is awarded once per tenant. */
  free_months: number;
  /** True when a month is earned but has no full-price rent row to land on yet. */
  free_month_pending: boolean;
  /** How many referrals are earned but currently have no rent row to land on. */
  reward_stalled_count: number;
  bonuses: ReferralBonus[];
}

export const EMPTY_REFERRALS: MyReferrals = {
  items: [],
  successful: 0,
  in_progress: 0,
  earned_cents: 0,
  goal: REFERRAL_FREE_MONTH_GOAL,
  toward_next: 0,
  free_months: 0,
  free_month_pending: false,
  reward_stalled_count: 0,
  bonuses: [],
};

type Lang = "en" | "de";

/** How far along the four-rung ladder this referral has climbed (1..4). */
export function stageIndex(stage: ReferralStage): number {
  const i = REFERRAL_STAGES.indexOf(stage);
  return i < 0 ? 1 : i + 1;
}

export function stageLabel(stage: ReferralStage, language: Lang): string {
  const en: Record<ReferralStage, string> = {
    applied: "Applied",
    approved: "Approved",
    signed: "Contract signed",
    rewarded: "Reward credited",
  };
  const de: Record<ReferralStage, string> = {
    applied: "Beworben",
    approved: "Genehmigt",
    signed: "Vertrag unterschrieben",
    rewarded: "Rabatt erfolgreich",
  };
  return language === "de" ? de[stage] : en[stage];
}

/**
 * The one-line status under each referral.
 *
 * A closed referral is called out first: a tenant staring at "Applied" for three
 * months deserves to know the application is not going anywhere, and saying so
 * is kinder than leaving them to guess.
 */
export function stageHint(
  item: Pick<
    ReferralItem,
    "stage" | "closed_at" | "reward_due_date" | "reward_stalled"
  >,
  language: Lang
): string {
  const de = language === "de";

  if (item.closed_at && item.stage !== "rewarded") {
    return de
      ? "Bewerbung nicht weiterverfolgt"
      : "Application did not proceed";
  }

  switch (item.stage) {
    case "applied":
      return de ? "Bewerbung wird geprüft" : "Application in review";
    case "approved":
      return de
        ? "Wartet auf den unterschriebenen Vertrag"
        : "Waiting for the signed contract";
    case "signed":
      // Only promise the credit when there is actually a rent row for it to
      // land on. A referrer who has moved out stays eligible and keeps the
      // EUR 100, but "credited to your next rent" is a date that never comes
      // for them — so say what is true and let them come to us.
      if (item.reward_stalled) {
        return de
          ? "100 € verdient – zurzeit keine offene Miete zur Verrechnung. Melde dich bei uns."
          : "€100 earned — no upcoming rent to credit it against. Get in touch with us.";
      }
      return item.reward_due_date
        ? de
          ? "100 € werden mit deiner nächsten Miete verrechnet"
          : "€100 will be credited to your next rent"
        : de
          ? "Verdient – wird mit deiner nächsten Miete verrechnet"
          : "Earned — credited to your next rent";
    case "rewarded":
      return de ? "100 € verrechnet" : "€100 applied";
  }
}

/** "JK" from "Jonas K." — the avatar initials, from the masked name only. */
export function referralInitials(name: string | null): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? (parts[1]?.[0] ?? "") : "";
  return (first + second).toUpperCase() || "?";
}

export function formatEuroFromCents(cents: number, language: Lang): string {
  return `€${(cents / 100).toLocaleString(
    language === "de" ? "de-DE" : "en-IE",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }
  )}`;
}

/**
 * The pre-written message the share sheet seeds.
 *
 * Carries the CODE, never a bare referral link — there is no "coliviq.de/r/CODE"
 * page, so offering one would be offering a dead URL. When a specific ROOM is
 * shared the link is that room's application page with the code in its query
 * string (see roomApplyUrl), so the friend lands on the form with the code
 * already filled in. The code is still spelled out in the text: a link can be
 * pasted without its query string, and the friend may apply from elsewhere.
 */
export function buildShareMessage(opts: {
  code: string;
  language: Lang;
  room?: { label: string; url: string } | null;
}): string {
  const { code, language, room } = opts;

  if (room) {
    return language === "de"
      ? `Hey! Bei mir im coliviq wird ein Zimmer frei: ${room.label} – ${room.url}\nMein Empfehlungscode ${code} ist im Link schon eingetragen – falls nicht, gib ihn bei der Bewerbung einfach an.`
      : `Hey! A room is opening up where I live at coliviq: ${room.label} – ${room.url}\nMy referral code ${code} is already in the link – if it isn't filled in, just enter it when you apply.`;
  }

  return language === "de"
    ? `Hey! Ich wohne bei coliviq – richtig gutes Co-Living. Nutz bei deiner Bewerbung meinen Empfehlungscode ${code}. Zimmer und Infos: ${publicOrigin()}/rooms`
    : `Hey! I live at coliviq — really good co-living. Use my referral code ${code} when you apply. Rooms and how it works: ${publicOrigin()}/rooms`;
}

/**
 * Origin for links a tenant sends to someone OUTSIDE the app.
 *
 * window.location.origin on purpose: on a preview deployment a shared link has
 * to point back at that deployment or the person testing it lands on
 * production and sees different rooms.
 */
export function publicOrigin(): string {
  if (typeof window === "undefined") return "https://coliviq.de";
  return window.location.origin;
}

/** The public listing for a room — the same URL the marketing site uses. */
export function roomListingUrl(roomId: number | string): string {
  return `${publicOrigin()}/property/${roomId}`;
}

/** Query parameter the application page reads the referral code from. */
export const REFERRAL_URL_PARAM = "ref";

/**
 * The application page for a room, with the referrer's code in the URL so
 * BookingConfirmation pre-fills the referral field. This is what a shared ROOM
 * links to; a code shared on its own has no room and therefore no link.
 */
export function roomApplyUrl(roomId: number | string, code: string): string {
  const params = new URLSearchParams({ roomId: String(roomId) });
  const normalised = normaliseReferralCode(code);
  if (normalised) params.set(REFERRAL_URL_PARAM, normalised);
  return `${publicOrigin()}/booking-confirmation?${params.toString()}`;
}

/**
 * "October" / "Oktober" for the rent row a referral credit would land on —
 * the same month the dashboard's "How it works" and the next-rent tile name,
 * so the promise and the tile never disagree.
 */
export function rentMonthLabel(
  dueDateIso: string | null | undefined,
  language: Lang
): string | null {
  if (!dueDateIso) return null;
  const part = dueDateIso.split("T")[0] ?? dueDateIso;
  const [y, m, d] = part.split("-").map((x) => Number(x));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toLocaleDateString(
    language === "de" ? "de-DE" : "en-GB",
    { month: "long" }
  );
}

/**
 * Step 4 of "How it works". Names the month when we know it — the tenant's next
 * not-yet-due rent row — and says "next" when there is none yet. "One-time" is
 * the point of the sentence: the EUR 100 is a credit on one month, not a new
 * rent.
 */
export function referralStepFourBody(
  month: string | null,
  language: Lang
): string {
  if (language === "de") {
    return month
      ? `Der einmalige Rabatt wird automatisch mit deiner ${month}-Miete verrechnet.`
      : "Der einmalige Rabatt wird automatisch mit deiner nächsten Miete verrechnet.";
  }
  return month
    ? `The one-time discount is applied automatically to your ${month} rent payment.`
    : "The one-time discount is applied automatically to your next rent payment.";
}

/**
 * The line under a credited next-rent amount on the tenant dashboard:
 * "€100 applied to your October rent thanks to a successful referral."
 */
export function referralCreditDashboardNote(opts: {
  cents: number;
  dueDateIso: string | null | undefined;
  language: Lang;
}): string {
  const { cents, dueDateIso, language } = opts;
  const amount = formatEuroFromCents(cents, language);
  const month = rentMonthLabel(dueDateIso, language);
  if (language === "de") {
    return month
      ? `${amount} Gutschrift auf deine ${month}-Miete – dank deiner erfolgreichen Empfehlung.`
      : `${amount} Gutschrift auf diese Miete – dank deiner erfolgreichen Empfehlung.`;
  }
  return month
    ? `${amount} applied to your ${month} rent thanks to a successful referral.`
    : `${amount} applied to this rent thanks to a successful referral.`;
}

/** The one shape a referral code takes: Coliviq-123456 (6 or, rarely, 8 digits). */
export const REFERRAL_CODE_PATTERN = /^Coliviq-\d{6,8}$/i;
export const REFERRAL_CODE_EXAMPLE = "Coliviq-483920";

/**
 * Normalise a code the way the database stores it, forgiving how people
 * actually retype one they were told.
 *
 * Every lookup compares upper() on both sides, so casing does not decide a
 * match — but the separator does, and "coliviq 483920" or "COLIVIQ483920"
 * would both miss without this. Since the stem is fixed and known, we put the
 * canonical hyphen back rather than making the applicant guess where it goes,
 * and settle on the readable mixed-case form so what they see echoes what
 * their friend read out. Anything unrecognised is passed through so an unknown
 * code still reads as an unknown code rather than being mangled into a
 * different — possibly real — one.
 */
export function normaliseReferralCode(raw: string): string {
  const bare = raw.replace(/[^A-Za-z0-9]/g, "");
  const match = bare.match(/^coliviq(\d+)$/i);
  return match ? `Coliviq-${match[1]}` : bare.toUpperCase();
}

/**
 * Progress toward the free month, clamped.
 *
 * Clamped at the top because an eleventh successful referral must not render a
 * 110%-wide progress bar spilling out of its track.
 */
export function referralProgress(successful: number, goal: number) {
  const safeGoal = goal > 0 ? goal : REFERRAL_FREE_MONTH_GOAL;
  const capped = Math.max(0, Math.min(successful, safeGoal));
  return {
    goal: safeGoal,
    capped,
    pct: Math.round((capped / safeGoal) * 100),
    reached: successful >= safeGoal,
  };
}

/**
 * What the apply form's referral field knows at any moment.
 *
 * Lives here rather than beside the component so that file exports a component
 * and nothing else — react-refresh cannot hot-reload a module that mixes the two.
 */
export interface ReferralCodeState {
  /** Normalised code, or "" when the field is empty. */
  code: string;
  /** null while unchecked or empty; otherwise the verdict from the server. */
  valid: boolean | null;
  referrerName: string | null;
}

export const EMPTY_REFERRAL_CODE_STATE: ReferralCodeState = {
  code: "",
  valid: null,
  referrerName: null,
};

/**
 * The referrals that unlocked a given free month.
 *
 * A milestone is "the Nth successful referral", so the ten people behind
 * milestone 10 are the first ten to sign, in the order they signed. Naming them
 * is the difference between "you earned a free month" and "Jonas, Sara and
 * eight others earned you a free month" — the second is the one a tenant
 * forwards to the friends who helped.
 *
 * Written for any milestone rather than hard-coded to the first ten: only
 * milestone 10 is awarded today (see record_referral_milestones), but the
 * ledger can express 20 and 30, and this should not be the thing that has to
 * change if that cap is ever lifted.
 */
export function referralsBehindMilestone(
  items: ReferralItem[],
  milestone: number
): ReferralItem[] {
  const successful = items
    .filter((i) => i.stage === "signed" || i.stage === "rewarded")
    .sort((a, b) =>
      (a.signed_at ?? a.created_at).localeCompare(b.signed_at ?? b.created_at)
    );
  return successful.slice(Math.max(0, milestone - 10), milestone);
}

/**
 * Why an entered referral code did not attach, in words the applicant can act
 * on.
 *
 * `attach_referral_to_booking` answers `{ok:false, reason}` rather than
 * raising, which keeps a bad code from costing anyone their application — but
 * it also means the failure is invisible unless a caller renders it. Both call
 * sites (the logged-in submit in BookingConfirmation and the new-account edge
 * function) previously only console.warn'd it, so a referrer who became
 * ineligible between the live check and submit lost EUR 100 with nobody told.
 *
 * Deliberately NOT phrased as an error the applicant has to fix: their
 * application is already in, and the code is a bonus for someone else. The
 * message says what happened and what to do, in that order.
 *
 * `self_referral` is the one reason worth naming precisely — it is the only
 * one the applicant can immediately understand and will otherwise read as a
 * bug ("my flatmate's code definitely works").
 *
 * Returns null for a reason that means the code DID attach, so callers can use
 * this as the sole decision.
 */
export function referralAttachWarning(
  reason: string | null | undefined,
  language: Lang
): string | null {
  if (!reason) return null;
  const de = language === "de";

  switch (reason) {
    case "self_referral":
      return de
        ? "Der eingegebene Empfehlungscode gehört zu deinem eigenen Konto und wurde deshalb nicht angewendet. Deine Bewerbung ist trotzdem eingegangen."
        : "That referral code belongs to your own account, so it wasn't applied. Your application went through as normal.";
    case "already_attached":
      return de
        ? "Für diese Bewerbung ist bereits ein anderer Empfehlungscode hinterlegt – der neue wurde nicht übernommen. Deine Bewerbung ist eingegangen."
        : "This application already has a different referral code on it, so the new one wasn't applied. Your application went through as normal.";
    default:
      // invalid_code, forbidden, booking_not_found, error, unknown — all of
      // them come down to the same thing for the applicant, and spelling out
      // which would leak whether a given code exists.
      return de
        ? "Dein Empfehlungscode konnte nicht angewendet werden. Deine Bewerbung ist trotzdem eingegangen – melde dich beim Support, wenn dich jemand empfohlen hat."
        : "We couldn't apply your referral code. Your application went through as normal — contact support if someone referred you, so they still get credited.";
  }
}

/**
 * One row of admin_referral_credit_notes(): which referral put a EUR 100
 * credit on which rent row. Staff-only and UNMASKED — never render this on a
 * tenant surface (the tenant-side equivalent is my_referrals()).
 */
export interface ReferralCreditNote {
  payment_id: string;
  referral_id: string;
  referred_name: string | null;
  room_label: string | null;
  amount_cents: number;
  applied_at: string | null;
}

/** Notes keyed by payment id, in the order the credits were applied. */
export function groupReferralCreditNotes(
  notes: readonly ReferralCreditNote[] | null | undefined
): Record<string, ReferralCreditNote[]> {
  const out: Record<string, ReferralCreditNote[]> = {};
  for (const n of notes ?? []) {
    if (!n?.payment_id) continue;
    (out[n.payment_id] ??= []).push(n);
  }
  return out;
}

/**
 * The admin-facing sentence for a credited rent row:
 * "Successfully referred Sara Tan to room Zimmer 4B · Wilhelmstraße 12.
 *  One-time €100 discount applied to this month's rent."
 */
export function referralCreditNoteText(
  note: Pick<
    ReferralCreditNote,
    "referred_name" | "room_label" | "amount_cents"
  >,
  lang: "de" | "en"
): string {
  const amount = formatEuroFromCents(note.amount_cents, lang);
  const name = (note.referred_name ?? "").trim();
  const room = (note.room_label ?? "").trim();
  if (lang === "de") {
    const who = name || "einen neuen Mieter";
    const where = room ? ` für ${room}` : "";
    return `Erfolgreiche Empfehlung: ${who}${where}. Einmaliger Rabatt von ${amount} auf diese Miete angerechnet.`;
  }
  const who = name || "a new tenant";
  const where = room ? ` to room ${room}` : "";
  return `Successfully referred ${who}${where}. One-time ${amount} discount applied to this month's rent.`;
}
